import { Link, useNavigate } from "react-router-dom";
import { Flame, ArrowRight, Sparkles, BookHeart, Waves } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";

export default function Landing() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!loading && user) {
            navigate(user.onboarded ? "/dashboard" : "/onboarding", { replace: true });
        }
    }, [loading, user, navigate]);

    return (
        <div className="min-h-screen cream-grain" data-testid="landing-page">
            {/* Header */}
            <header className="max-w-6xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <Flame className="w-5 h-5 text-brand-spark animate-flicker" strokeWidth={1.5} />
                    <span className="font-serif text-2xl text-brand-darkPurple tracking-tight">Lantern</span>
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/login">
                        <Button
                            data-testid="header-login-btn"
                            variant="ghost"
                            className="text-brand-darkPurple hover:bg-brand-darkPurple/5 rounded-full"
                        >
                            Sign in
                        </Button>
                    </Link>
                    <Link to="/register">
                        <Button
                            data-testid="header-register-btn"
                            className="bg-brand-darkPurple hover:bg-brand-darkPurpleSoft text-white rounded-full px-5"
                        >
                            Begin
                        </Button>
                    </Link>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-20 lg:pt-24 lg:pb-28">
                <div className="max-w-3xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-darkPurple/15 bg-white/60 text-xs text-brand-darkPurple/80 mb-6">
                        <Sparkles className="w-3 h-3 text-brand-spark" strokeWidth={2} />
                        A space to locate what's actually here — and take your next honest step.
                    </div>
                    <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-brand-darkPurple">
                        For the moment
                        <br />
                        <em className="italic font-normal text-brand-darkPurple/85">the old life</em>
                        <br />
                        no longer fits.
                    </h1>
                    <p className="mt-8 text-lg text-brand-charcoal/80 leading-relaxed max-w-2xl">
                        Lantern is a warm, private companion for women in the middle of a becoming — leaving a career, a marriage, a version of themselves. She listens like a wise friend who has walked through fire. She never rushes you toward the next thing.
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-3">
                        <Link to="/register">
                            <Button
                                data-testid="hero-cta-btn"
                                className="bg-brand-darkPurple hover:bg-brand-darkPurpleSoft text-white rounded-full px-7 py-6 text-base"
                            >
                                Light the lantern
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                        <Link to="/login">
                            <Button
                                data-testid="hero-secondary-btn"
                                variant="ghost"
                                className="text-brand-darkPurple hover:bg-brand-darkPurple/5 rounded-full px-6 py-6 text-base"
                            >
                                I've been here before
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Three-card feature preview */}
            <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-24">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white rounded-2xl p-8 border border-brand-darkPurple/10 shadow-soft-card">
                        <Flame className="w-5 h-5 text-brand-spark mb-4" strokeWidth={1.5} />
                        <h3 className="font-serif text-2xl text-brand-darkPurple mb-2">Untangle</h3>
                        <p className="text-sm text-brand-charcoal/75 leading-relaxed">Fact from story, from body, from pattern, from the desire underneath it all.</p>
                    </div>
                    <div className="bg-white rounded-2xl p-8 border border-brand-darkPurple/10 shadow-soft-card">
                        <Sparkles className="w-5 h-5 text-brand-spark mb-4" strokeWidth={1.5} />
                        <h3 className="font-serif text-2xl text-brand-darkPurple mb-2">Prepare</h3>
                        <p className="text-sm text-brand-charcoal/75 leading-relaxed">Build the clean ask, the boundary, the repair, the hard sentence you've been rehearsing.</p>
                    </div>
                    <div className="bg-white rounded-2xl p-8 border border-brand-darkPurple/10 shadow-soft-card">
                        <BookHeart className="w-5 h-5 text-brand-spark mb-4" strokeWidth={1.5} />
                        <h3 className="font-serif text-2xl text-brand-darkPurple mb-2">Stay with me</h3>
                        <p className="text-sm text-brand-charcoal/75 leading-relaxed">No strategy. No fixing. Just witnessed presence when the day asks too much.</p>
                    </div>
                </div>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-10 text-sm">
                    <div className="flex gap-3">
                        <Waves className="w-4 h-4 text-brand-spark mt-1 shrink-0" strokeWidth={1.75} />
                        <div>
                            <div className="font-medium text-brand-darkPurple mb-1">Pattern tracking</div>
                            <div className="text-brand-charcoal/70 leading-relaxed">The themes that keep circling back — surfaced gently, without judgment.</div>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <BookHeart className="w-4 h-4 text-brand-spark mt-1 shrink-0" strokeWidth={1.75} />
                        <div>
                            <div className="font-medium text-brand-darkPurple mb-1">Wisdom vault</div>
                            <div className="text-brand-charcoal/70 leading-relaxed">Save the lines that catch you. Return to your own truths later.</div>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <Sparkles className="w-4 h-4 text-brand-spark mt-1 shrink-0" strokeWidth={1.75} />
                        <div>
                            <div className="font-medium text-brand-darkPurple mb-1">Daily check-in</div>
                            <div className="text-brand-charcoal/70 leading-relaxed">One small pause. A mood, a note, an intention. That's enough.</div>
                        </div>
                    </div>
                </div>
            </section>

            <footer className="max-w-6xl mx-auto px-6 lg:px-10 py-8 border-t border-brand-darkPurple/10 text-xs text-brand-charcoal/60 flex flex-wrap justify-between gap-2">
                <div data-testid="footer-disclaimer">
                    <div>Lantern is not therapy. She's the friend who sees you between sessions.</div>
                    <div className="mt-1">Lantern isn't a therapist or a crisis service. If you're in immediate danger, please contact your local emergency number.</div>
                </div>
                <div>© Transition Companion AI</div>
            </footer>
        </div>
    );
}
